// Client for the NetSave AI endpoints on Cloud Run.
//
// Authentication is the Firebase ID token the browser already holds — the same
// pattern as qbCall/msCall in api.js. getIdToken() refreshes it when it is close
// to expiry, which matters here because the server deliberately refuses a token
// with under five minutes left rather than failing halfway through a turn.

import { auth } from "../firebase";
import { AI_BASE } from "../config/urls";

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error("You are signed out. Reload the page.");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${await user.getIdToken()}`,
  };
}

async function request(path, { method = "GET", body } = {}) {
  const res = await fetch(`${AI_BASE}${path}`, {
    method,
    headers: await authHeaders(),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Unexpected response from NetSave AI (${res.status}).`);
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status}).`);
  return data;
}

export const listSessions = (includeArchived = false) =>
  request(`/ai/sessions${includeArchived ? "?archived=1" : ""}`).then((d) => d.sessions);

export const createSession = (body) =>
  request("/ai/sessions", { method: "POST", body }).then((d) => d.session);

export const getSession = (id) => request(`/ai/sessions/${id}`);

export const patchSession = (id, patch) =>
  request(`/ai/sessions/${id}`, { method: "PATCH", body: patch });

export const removeSession = (id) =>
  request(`/ai/sessions/${id}`, { method: "DELETE" });

export const getUsage = () => request("/ai/usage");

export const getToolInfo = () => request("/ai/tools");

/**
 * Stream a turn, calling `onEvent` for each event as it arrives.
 *
 * The response is newline-delimited JSON rather than SSE: the request is a POST,
 * which EventSource cannot do, so it is read off the body stream directly. A
 * chunk can split a line anywhere, hence the buffer.
 */
async function stream(path, body, onEvent, signal) {
  const res = await fetch(`${AI_BASE}${path}`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    let message = `Request failed (${res.status}).`;
    try {
      message = JSON.parse(text).error || message;
    } catch {
      /* keep the generic message */
    }
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split("\n");
    // The last element is whatever came after the final newline — possibly half
    // a line, so it stays in the buffer.
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        onEvent(JSON.parse(line));
      } catch {
        // A malformed line should not abort a turn that is otherwise working.
      }
    }
  }

  if (buffer.trim()) {
    try {
      onEvent(JSON.parse(buffer));
    } catch {
      /* ignore a truncated trailing line */
    }
  }
}

export const sendMessage = (sessionId, text, onEvent, signal) =>
  stream(`/ai/sessions/${sessionId}/chat`, { text }, onEvent, signal);

export const confirmAction = (sessionId, draftId, decision, onEvent, signal) =>
  stream("/ai/confirm", { sessionId, draftId, decision }, onEvent, signal);
